<template>
  <b-modal
    ref="editorModal"
    :title="isEditing ? 'แก้ไขโฆษณา' : 'สร้างโฆษณาใหม่'"
    size="lg"
    scrollable
    no-close-on-backdrop
    @hidden="onHidden"
  >
    <b-form>
      <!-- Type + title -->
      <b-row>
        <b-col md="5">
          <b-form-group label="ประเภท">
            <b-form-select v-model="form.type" :options="typeOptions" />
          </b-form-group>
        </b-col>
        <b-col md="7">
          <b-form-group label="ชื่อโฆษณา">
            <b-form-input v-model="form.title" placeholder="ชื่อสำหรับอ้างอิงในรายงาน" />
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Image -->
      <template v-if="form.type === 'image'">
        <b-form-group label="ลิงก์รูปภาพ">
          <b-form-input v-model="form.content.imageUrl" placeholder="https://..." />
          <b-form-text>
            วางลิงก์รูปภาพที่เผยแพร่แล้ว (ยังไม่รองรับการอัปโหลดไฟล์ในหน้านี้)
          </b-form-text>
        </b-form-group>
        <b-form-group label="คำอธิบายรูป (alt text)">
          <b-form-input v-model="form.content.altText" />
        </b-form-group>
      </template>

      <!-- Video -->
      <template v-else-if="form.type === 'video'">
        <b-form-group label="ลิงก์วิดีโอ">
          <b-form-input v-model="form.content.videoUrl" placeholder="https://..." />
          <b-form-text>
            วางลิงก์วิดีโอที่เผยแพร่แล้ว (ยังไม่รองรับการอัปโหลดไฟล์ในหน้านี้)
          </b-form-text>
        </b-form-group>
        <b-form-group label="ภาพปก (thumbnail)">
          <b-form-input v-model="form.content.thumbnailUrl" placeholder="https://..." />
        </b-form-group>
      </template>

      <!-- Poll -->
      <template v-else-if="form.type === 'poll'">
        <b-form-group label="คำถาม">
          <b-form-input v-model="form.content.question" />
        </b-form-group>
        <b-form-group label="ตัวเลือก">
          <div
            v-for="(option, index) in form.content.options"
            :key="index"
            class="d-flex mb-2"
          >
            <b-form-input v-model="form.content.options[index]" />
            <b-button
              variant="outline-danger"
              class="ml-2"
              @click="removeOption(index)"
            >
              <i class="fas fa-times"></i>
            </b-button>
          </div>
          <b-button size="sm" variant="outline-primary" @click="addOption">
            <i class="fas fa-plus mr-1"></i> เพิ่มตัวเลือก
          </b-button>
        </b-form-group>
        <b-form-group label="ภาพพื้นหลัง">
          <b-form-input v-model="form.content.backgroundUrl" placeholder="https://..." />
        </b-form-group>
      </template>

      <!-- Testimonial -->
      <template v-else-if="form.type === 'testimonial'">
        <b-form-group label="ชื่อคนไข้">
          <b-form-input v-model="form.content.patientName" />
        </b-form-group>
        <b-form-group label="รีวิว">
          <b-form-textarea v-model="form.content.reviewText" rows="3" />
        </b-form-group>
        <b-row>
          <b-col md="6">
            <b-form-group label="รูปคนไข้">
              <b-form-input v-model="form.content.patientPhoto" placeholder="https://..." />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="คะแนน (1-5)">
              <b-form-input v-model.number="form.content.rating" type="number" min="1" max="5" />
            </b-form-group>
          </b-col>
        </b-row>
      </template>

      <!-- Information -->
      <template v-else-if="form.type === 'information'">
        <b-form-group label="หัวข้อ">
          <b-form-input v-model="form.content.title" />
        </b-form-group>
        <b-form-group label="ข้อความ">
          <b-form-textarea v-model="form.content.message" rows="3" />
        </b-form-group>
        <b-form-group label="ไอคอน (font-awesome class)">
          <b-form-input v-model="form.content.icon" placeholder="fas fa-info-circle" />
        </b-form-group>
      </template>

      <hr />

      <b-form-group label="ลิงก์ปลายทาง">
        <b-form-input v-model="form.linkUrl" placeholder="https://..." />
      </b-form-group>

      <b-row>
        <b-col md="4">
          <b-form-group label="ลำดับความสำคัญ">
            <b-form-input v-model.number="form.priority" type="number" />
            <b-form-text>ตัวเลขสูงกว่าจะแสดงก่อน</b-form-text>
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="เริ่มแสดง">
            <b-form-input v-model="form.startDt" type="date" />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="หยุดแสดง">
            <b-form-input v-model="form.endDt" type="date" />
          </b-form-group>
        </b-col>
      </b-row>

      <b-form-group label="ตำแหน่งที่แสดง">
        <b-form-checkbox v-model="form.showInCarousel" inline>
          แถบโฆษณา (carousel)
        </b-form-checkbox>
        <b-form-checkbox v-model="form.showInPopup" inline>
          ป๊อปอัป (popup)
        </b-form-checkbox>
      </b-form-group>

      <b-form-checkbox v-model="form.isActive" switch>
        เปิดใช้งาน
      </b-form-checkbox>
    </b-form>

    <b-alert v-if="error" variant="danger" show class="mt-3 mb-0">
      {{ error }}
    </b-alert>

    <template #modal-footer>
      <span class="text-muted mr-auto small">
        <i class="fas fa-info-circle mr-1"></i>
        บันทึกแล้วเผยแพร่ทันที ไม่ต้องกด Force Update Ads
      </span>
      <b-button variant="secondary" @click="hide">ยกเลิก</b-button>
      <b-button variant="primary" :disabled="saving" @click="save">
        <i v-if="saving" class="fas fa-spinner fa-spin mr-1"></i>
        บันทึก
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import AdsApiService from '@/services/AdsApiService';

/** Fields every ad carries, per type-specific content. */
const emptyForm = () => ({
  id: null,
  type: 'image',
  title: '',
  content: { options: [] },
  linkUrl: '',
  priority: 0,
  isActive: true,
  startDt: '',
  endDt: '',
  showInCarousel: true,
  showInPopup: false
});

export default {
  name: 'AdsEditorModal',
  data() {
    return {
      form: emptyForm(),
      saving: false,
      error: null,
      typeOptions: [
        { value: 'image', text: 'รูปภาพ' },
        { value: 'video', text: 'วิดีโอ' },
        { value: 'poll', text: 'โพล' },
        { value: 'testimonial', text: 'รีวิวคนไข้' },
        { value: 'information', text: 'ข้อมูลข่าวสาร' }
      ]
    };
  },
  computed: {
    isEditing() {
      return this.form.id !== null;
    }
  },
  methods: {
    /** @param {Object|null} ad an existing ad to edit, or null to create */
    show(ad = null) {
      this.error = null;
      this.form = ad
        ? {
            ...emptyForm(),
            ...ad,
            content: { options: [], ...(ad.content || {}) },
            // <input type="date"> only accepts YYYY-MM-DD.
            startDt: (ad.startDt || '').slice(0, 10),
            endDt: (ad.endDt || '').slice(0, 10)
          }
        : emptyForm();
      this.$refs.editorModal.show();
    },

    hide() {
      this.$refs.editorModal.hide();
    },

    onHidden() {
      this.form = emptyForm();
      this.error = null;
    },

    addOption() {
      this.form.content.options.push('');
    },

    removeOption(index) {
      this.form.content.options.splice(index, 1);
    },

    async save() {
      this.saving = true;
      this.error = null;
      try {
        const payload = {
          type: this.form.type,
          title: this.form.title || null,
          content: this.form.content,
          linkUrl: this.form.linkUrl || null,
          priority: Number(this.form.priority) || 0,
          isActive: this.form.isActive,
          startDt: this.form.startDt || null,
          endDt: this.form.endDt || null,
          showInCarousel: this.form.showInCarousel,
          showInPopup: this.form.showInPopup
        };

        if (this.isEditing) {
          await AdsApiService.updateAd(this.form.id, payload);
        } else {
          await AdsApiService.createAd(payload);
        }

        this.$emit('saved');
        this.hide();
      } catch (error) {
        // The server validates per ad type; surface its message rather than a
        // generic failure, since it names the missing field.
        this.error =
          error?.response?.data?.message ||
          error?.message ||
          'บันทึกไม่สำเร็จ';
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
